import { Dialog, DialogTitle, DialogContent, Box, DialogActions, Button } from "@mui/material"
import { useParams, useLocation } from "react-router-dom"
import { useContent } from "../../../logic"
import { useGetOrder } from "../../../logic/useApiQuery"
import { getCalStartDate, toTitleCase } from "../../../utils"
import { isAdhocPortalOrder } from "../../../utils/isAdhocPortalOrder"
import { ClosingTime, ClosingAddressUi } from "../ClosingDetailsSection"
import { ADDRESS_CHARS_REGEX } from "../ClosingHeader"
import { SanitizedHtml } from "../SanitizedHtml"

interface ClosingDetailsDialogProps {
  open: boolean;
  setClosingDetailsDialogOpen: (open: boolean) => void;
  setAddToCalendarOpen: (open: boolean) => void;
}

export function ClosingDetailsDialog({ open, setClosingDetailsDialogOpen, setAddToCalendarOpen }: ClosingDetailsDialogProps) { 
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const { SigningDetailsText } = useContent()

  if (!globalOrderData) {
    return null
  }

  if (isAdhocPortalOrder(globalOrderData.Order)) {
    return null
  }

  const allClosingDetailsHTML = globalOrderData?.Order.ClosingDetails?.AllClosingDetailsHTML
  const closingAddress = globalOrderData.Order.ClosingAddress
  const hasFullAddress = ADDRESS_CHARS_REGEX.test(closingAddress.Full || '')

  const hasGranularAddress =
    ADDRESS_CHARS_REGEX.test(closingAddress.Street || '') &&
    ADDRESS_CHARS_REGEX.test(closingAddress.City || '') &&
    ADDRESS_CHARS_REGEX.test(closingAddress.State || '') &&
    ADDRESS_CHARS_REGEX.test(closingAddress.Zip || '')

  const hasParseableTime = !!getCalStartDate(globalOrderData.Order)

  return <Dialog
    open={open}
    sx={{ '& .MuiDialog-paper': { borderRadius: '20px', minWidth: '350px' }}}
    onClose={() => setClosingDetailsDialogOpen(false)}
  >
    <DialogTitle>{toTitleCase(SigningDetailsText)}</DialogTitle>
    <DialogContent>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between', mt: 3,
          textAlign: 'center', ml: 'auto', mr: 'auto',
        }}
      >
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'left',
          '.sanitized-html': { textAlign: 'left' },
        }}>
          {allClosingDetailsHTML ?
            <SanitizedHtml html={allClosingDetailsHTML} /> :
            <>
              <ClosingTime order={globalOrderData?.Order} />
              <ClosingAddressUi address={closingAddress} hasGranularAddress={hasGranularAddress} hasFullAddress={hasFullAddress} />
            </>
          }
        </Box>
      </Box>
    </DialogContent>
    <DialogActions sx={{m: 1}}>
      <Button variant="rounded" onClick={() => setClosingDetailsDialogOpen(false)}>Close</Button>
      <Button
        variant="contained"
        onClick={() => {
          setClosingDetailsDialogOpen(false)
          setAddToCalendarOpen(true)
        }}
        sx={{
          display: (hasParseableTime && !allClosingDetailsHTML) ? 'initial' : 'none'
        }}
      >
        Add to calendar
      </Button>
    </DialogActions>
  </Dialog>
}
